/**
 * Помічник для розпаковки помилок з backend у людиночитані повідомлення.
 *
 * Backend повертає три формати `detail`:
 *  - **string**: наші HTTPException(status, detail="...") — українські
 *    повідомлення з конкретною причиною (захисний список, ліміт, тощо)
 *  - **list**: pydantic schema validation — масив об'єктів з полями
 *    {type, loc, msg, input}
 *  - **object**: Порція Б.3 атомарність — `{error, line, note}` коли
 *    bulk-імпорт упав на рядку. Показуємо як «Рядок N: причина.
 *    Імпорт скасовано повністю.»
 *
 * Цей помічник нормалізує всі формати у єдиний `string` для toast/dialog.
 */
import type { AxiosError } from 'axios';

interface PydanticError {
  type: string;
  loc: (string | number)[];
  msg: string;
  input?: unknown;
}

interface AtomicBulkError {
  error: string;
  line: number;
  note?: string;
}

interface BackendErrorBody {
  detail?: string | PydanticError[] | AtomicBulkError;
}

function isAtomicBulkError(d: unknown): d is AtomicBulkError {
  return (
    typeof d === 'object'
    && d !== null
    && !Array.isArray(d)
    && 'error' in d
    && 'line' in d
    && typeof (d as Record<string, unknown>).line === 'number'
  );
}

/**
 * Витягує людиночитане повідомлення з AxiosError'а backend'у.
 *
 * @example
 *   try {
 *     await accessRoles.create(payload);
 *   } catch (e) {
 *     toast.error(humanizeBackendError(e));
 *   }
 *
 * @param error — будь-який Error (бажано AxiosError)
 * @param fallback — повідомлення коли неможливо розпарсити (default
 *                  — українське «Помилка сервера»)
 */
export function humanizeBackendError(
  error: unknown,
  fallback: string = 'Помилка сервера. Спробуйте ще раз.',
): string {
  if (!error || typeof error !== 'object') return fallback;

  const ax = error as AxiosError<BackendErrorBody>;
  if (!ax.response?.data) {
    // Network/timeout
    if (ax.message) return ax.message;
    return fallback;
  }

  const detail = ax.response.data.detail;
  if (typeof detail === 'string') {
    return detail;
  }

  if (isAtomicBulkError(detail)) {
    // Б.3 атомарний bulk: показуємо номер рядка + причину + явний
    // акцент що НІЧОГО не залилось. Користувач має зрозуміти що
    // «скасовано повністю», не «частково».
    const noteSuffix = detail.note ? ` ${detail.note}` : '';
    return (
      `Рядок ${detail.line}: ${detail.error}.\n`
      + `Імпорт скасовано повністю — у БД нічого не змінено.${noteSuffix}`
    );
  }

  if (Array.isArray(detail)) {
    // Pydantic schema errors — формат [{type, loc, msg, ...}]
    const parts = detail.map((e) => {
      const field = Array.isArray(e.loc) ? e.loc.slice(1).join('.') : 'поле';
      return `${field}: ${e.msg}`;
    });
    return `Помилка валідації:\n${parts.join('\n')}`;
  }

  return fallback;
}

/**
 * Чи це 403 (недостатньо прав)?
 */
export function isForbiddenError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const ax = error as AxiosError;
  return ax.response?.status === 403;
}

/**
 * Чи це 422 (валідація)?
 */
export function isValidationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const ax = error as AxiosError;
  return ax.response?.status === 422;
}
